import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@backstage/core-plugin-api';
import { searchApiRef } from '@backstage/plugin-search-react';
import { SearchPalette } from './SearchPalette';
import type { SearchHit } from './searchModel';
import { toSearchHit } from './toSearchHit';

const PALETTE_LIMIT = 8;
// The full-results page is a fixed app route (see ./index.tsx). Navigating to
// the literal path keeps this container free of the new-frontend-system route
// resolver, which isn't available under the old-system test harness.
const SEARCH_PATH = '/search';

export interface SearchPaletteContainerProps {
  open: boolean;
  onClose: () => void;
}

/** Wires the quick-search palette to the search API and client-side routing. */
export const SearchPaletteContainer = ({
  open,
  onClose,
}: SearchPaletteContainerProps) => {
  const searchApi = useApi(searchApiRef);
  const navigate = useNavigate();

  const [term, setTerm] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [loading, setLoading] = useState(false);

  // Reset the query each time the palette closes.
  useEffect(() => {
    if (!open) {
      setTerm('');
      setHits([]);
    }
  }, [open]);

  // Debounced ad-hoc query against the API while the palette is open.
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (!open || !term) {
      setHits([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    if (timer.current) clearTimeout(timer.current);
    let cancelled = false;
    timer.current = setTimeout(() => {
      searchApi
        .query({ term, pageLimit: PALETTE_LIMIT })
        .then(res => !cancelled && setHits(res.results.map(toSearchHit)))
        .catch(() => !cancelled && setHits([]))
        .finally(() => !cancelled && setLoading(false));
    }, 200);
    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [open, term, searchApi]);

  const goToResults = () => {
    navigate(
      term ? `${SEARCH_PATH}?query=${encodeURIComponent(term)}` : SEARCH_PATH,
    );
    onClose();
  };

  return (
    <SearchPalette
      open={open}
      onClose={onClose}
      term={term}
      onTermChange={setTerm}
      hits={hits}
      loading={loading}
      onSelect={hit => {
        navigate(hit.location);
        onClose();
      }}
      onViewAll={goToResults}
    />
  );
};
